<template>
  <div id="app">

    <!-- Re-use the page-loader -->
    <div class="page-loading" :class="isBoostrapLoading ? 'page-loading--active': ''">
      <div v-if="isBoostrapLoading" class="spinner" />
    </div>

    <div class="container" v-if="errorBootstrapping">
        <div class="column has-text-centered">
          <h1>There was a problem loading the application</h1>
          <p>Please reload the page and contact us for support if this keeps reoccurring</p>
        </div>
    </div>

    <router-view v-if="!isBoostrapLoading && !errorBootstrapping"></router-view>
  </div>
</template>

<script>

import { setOptions, bootstrap } from 'vue-gtag'
import axios from "axios";
import store, {api} from "@/store/store";
import { mapGetters } from 'vuex'

import Vue from "vue";
import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";
import { CaptureConsole as CaptureConsoleIntegration } from "@sentry/integrations";

export default {
  name: 'App',
  provide () {
    return {
      // Provide impersonate user login to child components, note - may not work in vue 3?
      // https://stackoverflow.com/questions/60416153/making-vue-js-provide-inject-reactive
      checkAdminImpersonate: () => this.adminImpersonate,
    }
  },
  components: {
    // QuoteStart
  },
  data () {
    return {
      isBoostrapLoading: true,
      errorBootstrapping: false,
      adminImpersonate: false
    }
  },
  computed: {
  ...mapGetters([
      'getSessionId',
      'client'
    ])
  },
  async created () {

    // Store vue dev mode so we can turn off auto-redirect
    const vue_dev_mode_on =  process.env.NODE_ENV === 'development';

    // Set the document title
    document.title = "Car Insurance - initio";

    // TODO: check the implication of this
    axios.interceptors.response.use(undefined, (err) => {
      // this is intended to handle submitting to an endpoint that returns 403 (because session timed out)
      // any axios request that returns http error > 400 will also redirected (this may not be desired) - either to login or dashboard page
      //     hopefully with a PHP flash message set
      // for api requests sent to third-parties (stripe, locatio) which are also handled by axios

      return new Promise ( (resolve, reject) => {

        // we only want to catch /api requests on the same domain
        if ( err.request.responseURL.indexOf( window.location.hostname ) > -1 ) {
          if ((err.response.status >= 400) && err.response.config && !err.response.config.__isRetryRequest) {

            // Try grab
            const cli_num_or_unknown = this.client ? this.client.cli_num : 'unknown';

            // Redirect to the dashboard if its a 400 / 500
            let redirect_url = `/app/spa-issue/vehicle?response-code=${err.response.status}&uuid=${this.getSessionId()}&cli_num=${cli_num_or_unknown}`;

            // Disable the auto-redirecting when in vue-dev mode,
            // else you can't see the response from the server ( e.g what a 500 was or where it happened etc )
            if( vue_dev_mode_on ){
              // only do redirects when not in dev mode to make debugging easier
              window.alert(`VUE DEV MODE: PREVENTED AUTO-REDIRECT  (Browser would normally redirect to ${redirect_url})`);
            } else {
              // Redirect since not in dev mode
              console.error('API response error, redirecting to error page', redirect_url);
              window.location = redirect_url;
            }

            reject();
          }
        }
        throw err;
      });

    });

    // Handle setting up the tracking ( GA, Hotjar, Sentry,Adwords etc ) - ajax to get the correct tracking ID's based on environment
    try {
      const response = await axios.get(api.url + api.version + 'config/bootstrap');

      // Hide the loading icon
      this.isBoostrapLoading = false; // hide the page loading

      // We can't show the app, if we don't have the data from the boostrap lookup e.g correct captcha keys
      if( ! response.data || !response.data.results){
        console.error('Missing valid response for setting up boostrap information');
        this.errorBootstrapping = true;
      }


      const results = response.data.results;

      // Update the store config
      store.commit('updateAppConfig', response.data.results );

      // Notify if we don't have
      if( ! results.g_recapcha_v2_site_key ) {
        this.errorBootstrapping = true;
        console.error('no g_recapcha_v2_site_key set');
      }


      // Setup google analytics & adwords ( have dev account for this for staging )
      if( results.g_analytics_id ) {
        // console.log('Enable ga tracking');
        this.enableAnalyticsPlugin(results.g_analytics_id, results.g_analytics_ga4_id,   results.g_adwords_id, results.g_tagmanager_id, results.g_analytics_user_id)
      } else {
        console.log(' No valid GA ID');
      }

      // Manually call a sentry error ( must import sentry first ? )
      // Sentry.captureMessage("this is a debug message 2", "debug");

      // If the build is production && the results._env (PHP environment) is not local, then include tracking
      // E.g never track in 'dev' build mode, don't track when built but on local dev, but do track on staging & prod
      const sentrySampleRate = (process.env.NODE_ENV === "production" && (results._env !== 'local') ) ? 0.60 : 0; // 20% prod, 0% dev mode

      // Setup sentry

      // Don't run any kind of Sentry during 'serve' dev ( as it eats the console logs )
      if( process.env.NODE_ENV !== 'development'){
        Sentry.init({
          Vue,
          dsn: "https://9e0bdf53b3c14021ab400e16e5c6cd80@o196734.ingest.sentry.io/5707460",
          integrations: [
                new Integrations.BrowserTracing(),
                new CaptureConsoleIntegration(
                    {
                      // array of methods that should be captured
                      // defaults to ['log', 'info', 'warn', 'error', 'debug', 'assert']
                      levels: ['error']
                    }
                )
          ],
          environment: results._env ? results._env : 'unknown',
          // Set tracesSampleRate to 1.0 to capture 100%, 0.2 to capture 20% etc - start low.
          tracesSampleRate: sentrySampleRate, // set from above
          logError: true,  // Passing in logErrors is optional and is false if it is not provided. If you set it to true, Sentry will call Vue's original logError function as well.
          tracingOptions: {
            trackComponents: true,
          },
        });
      }

      // Only include a bunch of services when on prod
      if( results._env_is_prod ){

        // Setup Hotjar
        if( results.hotjar_id ) {
          // console.log('Enable Hotjar tracking');
          this.enableHotjarTracking(results.hotjar_id)
        } else {
          console.log(' No valid Hotjar tracking');
        }

        // Setup facebook pixel tracking
        if( results.fb_pixel ) {
          // console.log('Enable FB tracking');
          this.enableFBPixelTracking(results.fb_pixel)
        } else {
          console.log(' No valid facebook tracking');
        }


        if( results.zendesk_id ) {
          // console.log('Enable Zendesk');
          this.enableZendeskChat(results.zendesk_id)
        } else {
          console.log(' No valid zendesk tracking');
        }

      } else {
        console.log('Not running tracking setup code for Hotjar, FB, Zendesk on dev/staging');
      }

      // Secret key to determine if admin impersonating client
      this.adminImpersonate = response.data.results.imp_qz8Rv2 ?? false;

    } catch (e) {
      console.error(`Could not the bootstrapping data`, e);
      this.errorBootstrapping = true;
    }

  },
  methods: {
    enableAnalyticsPlugin (ga_id, ga_4_id, g_adwords_id, g_tagmanager_id, g_analytics_user_id) {

      setOptions({
        config: { id: ga_id },
      });
      const self = this;
      bootstrap().then((gtag) => {


        // Setup tag-manager
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            '//www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', g_tagmanager_id);


        // Setup adwords tracking // TODO REVIEW - don't think this will make adwords tracking actually work, as gets overriden below )
        gtag('config', g_adwords_id);

        // Manually Trigger page load for current page since we missed the boat when adding plugin to router
        // Fire the page load event, either with the user id tracking or without
        if( g_analytics_user_id ){
          gtag('config', ga_id, {
                    'page_path': window.location.pathname,
                    'user_id': `${g_analytics_user_id}`
          });
        } else {
          gtag('config', ga_id, {'page_path': window.location.pathname });
        }

        // Setup GA 4 ( Mid Feb 2023 )
        if( g_analytics_user_id ){
          gtag('config', ga_4_id, {
            'page_path': window.location.pathname,
            'user_id': `${g_analytics_user_id}`
          });
        } else {
          gtag('config', ga_4_id, {'page_path': window.location.pathname });
        }

      }).catch(_ => {
          // throws error when ad-blocked
      });
    },
    enableHotjarTracking(hotjar_id){
      // Hotjar Tracking Code -->
      (function (h, o, t, j, a, r) {
        h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) }
        h._hjSettings = {
          hjid: hotjar_id, // Hotjar ID
          hjsv: 6 // Hotjar Snippet Version
        }
        a = o.getElementsByTagName('head')[0]
        r = o.createElement('script'); r.async = 1
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv
        a.appendChild(r)
      })(
          window,
          document,
          'https://static.hotjar.com/c/hotjar-',
          '.js?sv='
      )

    },
    enableFBPixelTracking(fb_pixel){
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window,document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
      window.fbq('init', fb_pixel);
      window.fbq('track', 'PageView');

    },
    enableZendeskChat(zendesk_id){
      window.$zopim||(function(d,s){var z=window.$zopim=function(c){
        z._.push(c)},$=z.s=
          d.createElement(s),e=d.getElementsByTagName(s)[0];z.set=function(o){z.set.
      _.push(o)};z._=[];z.set._=[];$.async=!0;$.setAttribute('charset','utf-8');
        $.src=`https://v2.zopim.com/?${zendesk_id}`;z.t=+new Date;$.
            type='text/javascript';e.parentNode.insertBefore($,e)})(document,'script');
    }
  }
}

</script>

<style>

</style>
